import * as Cesium from "cesium";
import Type from "../Types";
import { createUniqueId, showToast } from "../Utility";

// find entity that has distance + yaw
function findChartableFromCzml(czml) {
  for (const item of czml) {
    if (!item || typeof item !== "object") continue;
    if (!item.properties) continue;
    const p = item.properties;
    const hasDist = !!p.distance_m;
    const hasYaw = !!p.yaw_radians;
    if (hasDist && hasYaw) {
      return {
        entityId: item.id,
        distanceProp: "distance_m",
        yawProp: "yaw_radians",
      };
    }
  }
  return null;
}

// turn CZML property to [{t, v}, ...]
function extractTimeSeries(prop) {
  // prop expected: { epoch: "2025-01-01T00:00:00Z", number: [t0, v0, t1, v1, ...] }
  if (!prop || !Array.isArray(prop.number)) return [];
  const epoch = prop.epoch || null;
  const arr = prop.number;
  const out = [];
  for (let i = 0; i < arr.length; i += 2) {
    out.push({
      t: arr[i], // seconds from epoch
      v: arr[i + 1], // value
      epoch, // keep epoch for later
    });
  }
  return out;
}

// merge distance + yaw by index
function buildChartData(czml, chartMeta) {
  if (!chartMeta) return null;
  const entity = czml.find((e) => e.id === chartMeta.entityId);
  if (!entity || !entity.properties) return null;

  const distProp = entity.properties[chartMeta.distanceProp];
  const yawProp = entity.properties[chartMeta.yawProp];
  const distSeries = extractTimeSeries(distProp);
  const yawSeries = extractTimeSeries(yawProp);

  // assume same length and order
  const len = Math.min(distSeries.length, yawSeries.length);
  const rows = [];
  for (let i = 0; i < len; i++) {
    rows.push({
      timeSec: distSeries[i].t, // x time
      distance_m: distSeries[i].v, // x axis
      yaw_rad: yawSeries[i].v, // y axis (radian)
      epoch: distSeries[i].epoch, // keep epoch
    });
  }
  return {
    entityId: chartMeta.entityId,
    rows, // [{timeSec, distance_m, yaw_rad, epoch}, ...]
  };
}

export async function loadCzmlTypeAsync({
  text = null,
  czmlData = null,
  fileName,
  viewerRef,
  setMapObjects,
  modelUrl = "https://matrix.gatik.ai/static/gatiktruck.glb",
  modelEntityId = null,
  modelScale = 1.0,
  modelMinPixel = 64,
}) {
  try {
    let czml = czmlData;
    if (!czml && text) {
      czml = JSON.parse(text);
    }
    if (!czml) {
      showToast("Invalid CZML data", Type.NOTIFICATION.ERROR);
      return;
    }

    const viewer = viewerRef.current;
    if (!viewer) {
      showToast("Viewer is not ready", Type.NOTIFICATION.ERROR);
      return;
    }

    const scene = viewerRef.current.scene;
    scene.light = new Cesium.DirectionalLight({
      direction: new Cesium.Cartesian3(0.3, 0.3, -0.9),
    });
    scene.highDynamicRange = false;
    scene.imageBasedLightingFactor = new Cesium.Cartesian2(1.5, 1.5);

    const id = createUniqueId();

    const chartMeta = findChartableFromCzml(czml);
    const chartData = buildChartData(czml, chartMeta);

    setMapObjects((prev) => [
      ...prev,
      {
        id,
        name: fileName,
        type: Type?.ASSET?.CZML ?? "czml",
        file_name: fileName,
        file_type: ".czml",
        visible: true,
        dataSource: null,
        loading: true,
        canShowYawChart: !!chartData, 
        chartMeta,
        chartData, 
      },
    ]);

    const dataSource = await Cesium.CzmlDataSource.load(czml);
    await viewer.dataSources.add(dataSource);

    if (dataSource.clock) {
      const vc = viewer.clock;
      const dc = dataSource.clock;
      vc.startTime = dc.startTime.clone();
      vc.stopTime = dc.stopTime.clone();
      vc.currentTime = dc.currentTime.clone();
      vc.clockRange = Cesium.ClockRange.CLAMPED;
      vc.multiplier = dc.multiplier;
      vc.shouldAnimate = false;
    } else {
      viewer.clock.shouldAnimate = false;
    }

    if (modelUrl) {
      let targetEntity = null;
      if (modelEntityId) {
        targetEntity = dataSource.entities.getById(modelEntityId);
      } else {
        targetEntity = dataSource.entities.values.find((e) => e.id !== "document");
      }

      if (targetEntity) {
        targetEntity.point = undefined;
        targetEntity.billboard = undefined;
        targetEntity.label = undefined;
        targetEntity.model = new Cesium.ModelGraphics({
          uri: modelUrl,
          scale: modelScale,
          minimumPixelSize: modelMinPixel,
        });
        if (targetEntity.model?.shadows !== undefined) targetEntity.model.shadows = Cesium.ShadowMode.DISABLED;

        if (Cesium.CustomShader && Cesium.LightingModel) {
          targetEntity.model.customShader = new Cesium.CustomShader({
            lightingModel: Cesium.LightingModel.UNLIT,
          });
        }

        if (!targetEntity.orientation && targetEntity.position) {
          targetEntity.orientation = new Cesium.VelocityOrientationProperty(targetEntity.position);
        }
      }
    }

    viewer.flyTo(dataSource);

    setMapObjects((prev) => {
      const copy = [...prev];
      const idx = copy.findIndex((o) => o.id === id);
      if (idx !== -1) {
        copy[idx] = {
          ...copy[idx],
          dataSource,
          loading: false,
        };
      }
      return copy;
    });

    return dataSource;
  } catch (err) {
    showToast(err.message || "Failed to load CZML", Type.NOTIFICATION.ERROR);
    console.error(err);
  }
}
